import { m } from '@hub/messages';
import { definePageRoute, defineRedirectRoute } from '@hub/router/definePageRoute';
import { createRoute, type DomainParents, defineDomainRoutes } from '@hub/router/domain-routes';
import { requireAuthentication } from '@hub/router/pages/_authenticated.guard';
import { loadBirthdayContestDestination } from './birthday-contest/loadBirthdayContestDestination';
import { BirthdayContestPaths, FunkeTvDigitalPaths, WmQuizPaths } from './route.paths';

export default function createCampaignRoutes(parents: DomainParents) {
  const funkeTvDigitalStartRoute = definePageRoute(parents, {
    path: FunkeTvDigitalPaths.start,
    title: m.funke_tv_digital_meta_title,
    description: m.funke_tv_digital_meta_description,
    page: () => import('./funke-tv-digital/FunkeTvDigital.page'),
    flag: 'page-funke-tv-digital',
    footerVariant: 'minimal',
    theme: 'dark',
    hideHeaderSupport: true,
  });

  const funkeTvDigitalParticipationTermsRoute = definePageRoute(parents, {
    path: FunkeTvDigitalPaths.participationTerms,
    title: m.funke_tv_digital_participation_terms_title,
    description: m.funke_tv_digital_participation_terms_description,
    page: () => import('./funke-tv-digital/ParticipationTerms.page'),
    flag: 'page-funke-tv-digital',
    footerVariant: 'minimal',
    theme: 'dark',
    hideHeaderSupport: true,
  });

  const wmQuizStartRoute = definePageRoute(parents, {
    path: WmQuizPaths.start,
    title: m.wm_quiz_finished_title,
    description: m.wm_quiz_finished_meta_description,
    page: () => import('./wm-quiz/QuizStart.page'),
    flag: 'page-wm-quiz',
    footerVariant: 'minimal',
    theme: 'dark',
    hideHeaderSupport: true,
  });

  const wmQuizParticipationTermsRoute = definePageRoute(parents, {
    path: WmQuizPaths.participationTerms,
    title: m.wm_quiz_participation_terms_title,
    description: m.wm_quiz_participation_terms_description,
    page: () => import('./wm-quiz/ParticipationTerms.page'),
    flag: 'page-wm-quiz',
    footerVariant: 'minimal',
    theme: 'dark',
    hideHeaderSupport: true,
  });

  const legacyWmQuizStartRedirect = defineRedirectRoute(parents, {
    from: WmQuizPaths.legacyStart,
    to: WmQuizPaths.start,
  });

  const legacyWmQuizParticipationTermsRedirect = defineRedirectRoute(parents, {
    from: WmQuizPaths.legacyParticipationTerms,
    to: WmQuizPaths.participationTerms,
  });

  // Renders nothing, so no `page`/`title` — `definePageRoute`'s standard shape doesn't fit. Sits on
  // `parents.bare` rather than `parents.authenticated` so an anonymous visit never flashes the
  // authenticated shell chrome before `requireAuthentication` redirects it to login.
  // `loadBirthdayContestDestination` always throws an external redirect from the loader, so
  // `preload: false` keeps a hover from firing the subscription fetch and login round-trip.
  const birthdayContestLandingRoute = createRoute({
    getParentRoute: () => parents.bare,
    path: BirthdayContestPaths.landing.path,
    validateSearch: BirthdayContestPaths.landing.search,
    preload: false,
    beforeLoad: requireAuthentication,
    loaderDeps: ({ search }) => ({ marker: search.from }),
    loader: loadBirthdayContestDestination,
  });

  return defineDomainRoutes({
    authenticated: [] as const,
    public: [
      funkeTvDigitalStartRoute,
      funkeTvDigitalParticipationTermsRoute,
      wmQuizStartRoute,
      wmQuizParticipationTermsRoute,
      legacyWmQuizStartRedirect,
      legacyWmQuizParticipationTermsRedirect,
    ] as const,
    bare: [birthdayContestLandingRoute] as const,
  });
}
